import { $getSelection, $isRangeSelection, $getRoot, $isTextNode, TextNode } from 'lexical';
import { $convertFromMarkdownString, Transformer } from '@lexical/markdown';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';

export const replacePlaceholdersWithImages = (md: string, imgs: MarkdownImage[]) => {
  return imgs.reduce(
    (acc, img) => acc.replaceAll(`{image:${img.id}}`, `![Image](${img.encodedImage})`),
    md ?? '',
  );
};

export const replaceImagesWithPlaceholders = (md: string, imgs: MarkdownImage[]) => {
  return imgs.reduce((acc, img) => {
    return acc.split(`![Image](${img.encodedImage})`).join(`{image:${img.id}}`);
  }, md);
};

export function $setEditorValue(markdown: string, transformers: Transformer[]): void {
  const root = $getRoot();
  root.clear();
  $convertFromMarkdownString(markdown, transformers);
}

const OPENING_TAG = '<replacement>';
const CLOSING_TAG = '</replacement>';

type InsertedMarkers = {
  openingNode: TextNode;
  openingOffset: number;
  closingNode: TextNode;
  closingOffset: number;
};

export function $insertReplacementMarkers(): InsertedMarkers | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return null;
  }

  const startPoint = selection.isBackward() ? selection.focus : selection.anchor;
  const endPoint = selection.isBackward() ? selection.anchor : selection.focus;

  const selectedNodes = selection.getNodes();
  if (selectedNodes.length === 0) return null;

  const firstTextNode = selectedNodes.find((n): n is TextNode => $isTextNode(n));
  const lastTextNode = [...selectedNodes].reverse().find((n): n is TextNode => $isTextNode(n));

  if (!firstTextNode || !lastTextNode) return null;

  const startOffset =
    firstTextNode.getKey() === startPoint.getNode().getKey() ? startPoint.offset : 0;
  const endOffset =
    lastTextNode.getKey() === endPoint.getNode().getKey()
      ? endPoint.offset
      : lastTextNode.getTextContentSize();

  if (firstTextNode === lastTextNode) {
    firstTextNode.spliceText(startOffset, 0, OPENING_TAG, true);
    firstTextNode.spliceText(endOffset + OPENING_TAG.length, 0, CLOSING_TAG, true);
    return {
      openingNode: firstTextNode,
      openingOffset: startOffset,
      closingNode: firstTextNode,
      closingOffset: endOffset + OPENING_TAG.length,
    };
  } else {
    lastTextNode.spliceText(endOffset, 0, CLOSING_TAG, true);
    firstTextNode.spliceText(startOffset, 0, OPENING_TAG, true);
    return {
      openingNode: firstTextNode,
      openingOffset: startOffset,
      closingNode: lastTextNode,
      closingOffset: endOffset,
    };
  }
}

export function $removeReplacementMarkers(markers: InsertedMarkers): void {
  const { openingNode, openingOffset, closingNode, closingOffset } = markers;

  if (openingNode === closingNode) {
    closingNode.spliceText(closingOffset, CLOSING_TAG.length, '', true);
    openingNode.spliceText(openingOffset, OPENING_TAG.length, '', true);
  } else {
    closingNode.spliceText(closingOffset, CLOSING_TAG.length, '', true);
    openingNode.spliceText(openingOffset, OPENING_TAG.length, '', true);
  }
}
